import { ReactElement } from 'react';
import { useCurrentFrame, useVideoConfig } from 'remotion';

// Animations
import { calculateDynamicRotation } from '@/animations';

// Types
import { GridConfig, PlusSign } from '../types';

// Utils
import { createGridToPixel } from '../utils';

interface PlusSignsProps {
  plusSigns: PlusSign[];
  defaultColor: string;
  grid: GridConfig;
  width: number;
  height: number;
}

/**
 * Renders animated plus signs at grid positions with per-item rotation.
 *
 * @param props
 * @param props.plusSigns - List of plus sign definitions (position, size, color).
 * @param props.defaultColor - Fallback stroke color when a plus sign omits color.
 * @param props.grid - Grid configuration used for coordinate conversion.
 * @param props.width - Pixel width of the drawing area.
 * @param props.height - Pixel height of the drawing area.
 */
export const PlusSignsComponent = ({
  plusSigns,
  defaultColor,
  grid,
  width,
  height,
}: PlusSignsProps): ReactElement => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const gridToPixel = createGridToPixel(grid, width, height);

  return (
    <>
      {plusSigns.map((plus, index) => {
        // Convert the grid position to pixel coordinates in the SVG space.
        const pixel = gridToPixel(plus.position);
        // Use per-item overrides with sensible defaults.
        const size = plus.size ?? 30;
        const strokeWidth = plus.strokeWidth ?? 10;
        const color = plus.color ?? defaultColor;

        // Calculate dynamic rotation for this plus sign.
        const rotation = calculateDynamicRotation(frame, fps, index);

        return (
          <g
            key={`plus-${index}`}
            transform={`rotate(${rotation} ${pixel.x} ${pixel.y})`}
          >
            {/* Horizontal stroke */}
            <line
              x1={pixel.x - size / 2}
              y1={pixel.y}
              x2={pixel.x + size / 2}
              y2={pixel.y}
              stroke={color}
              strokeWidth={strokeWidth}
            />
            {/* Vertical stroke */}
            <line
              x1={pixel.x}
              y1={pixel.y - size / 2}
              x2={pixel.x}
              y2={pixel.y + size / 2}
              stroke={color}
              strokeWidth={strokeWidth}
            />
          </g>
        );
      })}
    </>
  );
};
